import { EditIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useContext, useEffect, useState } from 'react'
import { ENV, ScoresContext } from '../routes/ScoreListing'

function WeekSettingsModal() {
  const context = useContext(ScoresContext)
  const disclosure = useDisclosure()
  const toast = useToast()

  const [url, setUrl] = useState('')
  const [weekNumInput, setWeekNumInput] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  if (!context) {
    throw new Error('ScoresContext is not available for WeekSettingsModal')
  }

  const { weekNum: routeWeekNum, weekDocument, updateWeekDocument } = context

  useEffect(() => {
    if (!disclosure.isOpen) {
      return
    }

    if (weekDocument) {
      setUrl(weekDocument.url ?? '')
      setWeekNumInput(String(weekDocument.weekNum ?? routeWeekNum))
    } else {
      setUrl('')
      setWeekNumInput(String(routeWeekNum))
    }
  }, [disclosure.isOpen, weekDocument, routeWeekNum])

  const handleOpen = () => {
    if (!disclosure.isOpen) {
      if (weekDocument) {
        setUrl(weekDocument.url ?? '')
        setWeekNumInput(String(weekDocument.weekNum ?? routeWeekNum))
      } else {
        setUrl('')
        setWeekNumInput(String(routeWeekNum))
      }
    }
    disclosure.onOpen()
  }

  const handleSave = async () => {
    if (!url.trim()) {
      toast({
        title: 'Missing URL',
        description: 'Enter a valid URL before saving.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    const parsedWeekNum = parseInt(weekNumInput, 10)
    if (Number.isNaN(parsedWeekNum)) {
      toast({
        title: 'Invalid week number',
        description: 'Enter a valid week number.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    if (!window.confirm('Are you sure you want to save these changes?')) {
      return
    }

    setIsSaving(true)
    try {
      await updateWeekDocument({ url: url.trim(), weekNum: parsedWeekNum })
      toast({
        title: 'Week updated',
        description: 'Firestore record updated successfully.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      })
      disclosure.onClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      toast({
        title: 'Error while saving',
        description: message,
        status: 'error',
        duration: 6000,
        isClosable: true,
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <Tooltip hasArrow label={weekDocument ? 'Edit week details' : 'Waiting for week data'}>
        <IconButton
          aria-label="Edit week"
          icon={<EditIcon />}
          variant="ghost"
          color="white"
          borderWidth="1px"
          borderColor="whiteAlpha.500"
          _hover={{ bg: 'whiteAlpha.300' }}
          size="sm"
          onClick={handleOpen}
          isDisabled={!weekDocument}
        />
      </Tooltip>

      <Modal isOpen={disclosure.isOpen} onClose={disclosure.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Week parameters</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  Changing these parameters can be disruptive. Proceed carefully and ensure you
                  understand the consequences.
                </AlertDescription>
              </Alert>

              <Text fontSize="sm" color="gray.600">
                You are editing Firestore document{' '}
                <strong>{`scores_${ENV}/week${routeWeekNum}`}</strong>.
              </Text>

              <FormControl>
                <FormLabel>Forum URL</FormLabel>
                <Input value={url} type="url" onChange={(event) => setUrl(event.target.value)} />
              </FormControl>

              <FormControl>
                <FormLabel>Week number</FormLabel>
                <Input
                  value={weekNumInput}
                  type="number"
                  onChange={(event) => setWeekNumInput(event.target.value)}
                />
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={disclosure.onClose}>
              Close
            </Button>
            <Button colorScheme="yellow" onClick={handleSave} isLoading={isSaving}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default WeekSettingsModal
